import "./orderConfirmation.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, GridContainer, GridItem, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackPopUpAlignment,
} from "common/components";
import { InlineUserReference } from "common/components/InlineUserReference";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { CatalogItemDomains, CreateOrderResponseContent } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

export interface OrderConfirmationState extends CreateOrderResponseContent {
  orderRedirectURL?: string;
}

const OrderConfirmation: FC = () => {
  const location = useLocation();
  const history = useHistory();

  const localState: OrderConfirmationState | undefined = location.state as OrderConfirmationState;

  useEffect(() => {
    if (localState?.canBeAutoApproved && localState?.orderRedirectURL) {
      const timer = setTimeout(() => {
        window.open(localState.orderRedirectURL, "_blank");
      }, 3000);

      return () => clearTimeout(timer);
    }
  });

  if (localState === undefined || localState.orderReference === undefined) {
    history.push("/");
    return null;
  }
  const isHardwareOrder = localState.itemType && localState.itemType === CatalogItemDomains.HARDWARE;

  const orderReference = localState.orderReference;

  const onViewOrderClick = () => {
    history.push(`/orders?tab=${isHardwareOrder ? "1" : "0"}#${localState.orderId}`);
  };

  const onReturnHomeClick = () => {
    history.push("/");
  };

  const getOrderConfirmationDescription = () => {
    if (isHardwareOrder) {
      return getString("orderConfirmation.confirmationDescription");
    }

    if (localState.canBeAutoApproved) {
      return getString("orderConfirmation.autoApprovedOrder.instructions");
    }

    if (localState.requiredSTPApprover) {
      // Shows the STP approver
      // TODO to deprecate when no special approval
      const approverReferenceComponent = <InlineUserReference userReference={localState.requiredSTPApprover!} />;

      return (
        <>
          {`${getString("orderConfirmation.managerRequest.instructions.part1")} `} {approverReferenceComponent}{" "}
          {`${getString("orderConfirmation.managerRequest.instructions.part2")}`}
        </>
      );
    }

    if (!localState.requiredSTPApprover && !localState.canBeAutoApproved) {
      return getString("orderConfirmation.amazonApprovals.instructions");
    }

    // TODO display the approvals
    return "";
  };

  const renderHeader = () => {
    return (
      <Row gridGap="S300">
        <IconCheckCircleFill color="green70" size={IconSize.Large} title={getString("orderConfirmation.success")} />
        <Text fontSize="T300" fontWeight="bold" data-cy="confirmation-title">
          {getString("orderConfirmation.confirmationTitle")}
        </Text>
      </Row>
    );
  };

  const renderConfirmationContent = () => {
    return (
      <Col gridGap="S300">
        <Text fontSize="T200" fontWeight="bold">
          {`${getString("orderConfirmation.orderId")}${orderReference}`}
        </Text>
        <Text fontSize="T200" fontWeight="regular">
          {getOrderConfirmationDescription()}
        </Text>
      </Col>
    );
  };

  const renderButtons = () => {
    return (
      <Row gridGap="S300">
        <Button data-testid="view-order-button" variant={ButtonVariant.Secondary} onClick={onViewOrderClick}>
          {getString("orderConfirmation.viewOrderButton")}
        </Button>
        <Button data-testid="return-home-button" variant={ButtonVariant.Tertiary} onClick={onReturnHomeClick}>
          {getString("orderConfirmation.returnHomeButton")}
        </Button>
      </Row>
    );
  };

  const renderCFM = () => {
    // Software config
    let itemType = "software";
    let cfmModuleName = CFMModuleName.orderFlow;

    // Hardware config
    if (isHardwareOrder) {
      itemType = "hardware";
      cfmModuleName = CFMModuleName.hardwareOrderConfirmation;
    }

    return (
      <Row data-testid={`${itemType}-order-confirmation-cfm`}>
        <CustomerFeedback
          textSize="T200"
          moduleName={cfmModuleName}
          alignment={CustomerFeedbackAlignment.Left}
          position={CustomerFeedbackIconPosition.Left}
          popUpXAlignment={CustomerFeedbackPopUpAlignment.Right}
        />
      </Row>
    );
  };

  return (
    <PageMainContainer paddingTop="100px">
      <GridContainer>
        <GridItem lg={2} />
        <GridItem lg={2}>
          {/* Illustration */}
          <View display="flex" justifyContent="center" width="100%" height="100%">
            <div className="earth-icon" />
          </View>
        </GridItem>
        <GridItem lg={6}>
          <Col gridGap="S500">
            {/* Icon & title */}
            {renderHeader()}

            {/* Order Id & description */}
            {renderConfirmationContent()}

            {/* Buttons */}
            {renderButtons()}

            {/* Customer Feedback Module */}
            {renderCFM()}
          </Col>
        </GridItem>
        <GridItem lg={2} />
      </GridContainer>
    </PageMainContainer>
  );
};

export default OrderConfirmation;
